import { useEffect, useState } from "react";
import { Fingerprint, Copy, Trash2 } from "lucide-react";
import { api, errorMessage } from "../api";
import { Field, Modal, Notice } from "./Common";
type Key = { id: string; label: string; created_at: number };
export function PasskeySettings() {
  const [keys, setKeys] = useState<Key[]>([]),
    [invite, setInvite] = useState<{ code: string; expires: number } | null>(
      null,
    ),
    [remove, setRemove] = useState<Key | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [copied, setCopied] = useState(false);
  const load = async () => setKeys(await api<Key[]>("/passkeys"));
  useEffect(() => {
    void load().catch((e) => setError(errorMessage(e)));
  }, []);
  const link = invite ? `${location.origin}/#setup=${invite.code}` : "";
  return (
    <section className="settings-section">
      <h2>
        <Fingerprint size={20} /> Passkeys
      </h2>
      <p>
        Use Face ID, Touch ID or a device passcode to sign in. Passkeys saved in
        iCloud Keychain can work across your Apple devices.
      </p>
      {error && <Notice type="error">{error}</Notice>}
      {keys.map((key) => (
        <div className="token-row" key={key.id}>
          <div>
            <strong>{key.label}</strong>
            <p className="muted small">
              Added{" "}
              {new Date(key.created_at * 1000).toLocaleDateString("en-GB")}
            </p>
          </div>
          <button
            className="icon-button"
            disabled={busy || keys.length < 2}
            aria-label={`Remove ${key.label}`}
            onClick={() => setRemove(key)}
          >
            <Trash2 size={18} />
          </button>
        </div>
      ))}
      <button
        className="secondary inline"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError("");
          try {
            setInvite(await api("/passkeys/invite", "POST", {}));
            setCopied(false);
          } catch (e) {
            setError(errorMessage(e));
          } finally {
            setBusy(false);
          }
        }}
      >
        Set up another device
      </button>
      {invite && (
        <div className="notice">
          <p>
            <strong>One-time setup link</strong>
          </p>
          <p className="small">
            Open this link on your other device and create a passkey. Anyone
            with this link can join your workspace. It expires at{" "}
            {new Date(invite.expires * 1000).toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            })}
            .
          </p>
          <Field label="Private setup link">
            <input value={link} readOnly autoComplete="off" />
          </Field>
          <button
            className="secondary inline"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(link);
                setCopied(true);
              } catch {
                setError("Copy the setup link from the field above.");
              }
            }}
          >
            <Copy size={16} />
            {copied ? "Copied" : "Copy setup link"}
          </button>
        </div>
      )}
      {keys.length === 1 && (
        <p className="muted small">
          Keep at least one passkey. Add a second passkey before removing this
          one.
        </p>
      )}
      {remove && (
        <Modal
          title={`Remove ${remove.label}?`}
          onClose={() => setRemove(null)}
        >
          <p>
            This passkey will lose access and its active sessions will be signed
            out.
          </p>
          <button
            className="danger"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api(
                  `/passkeys/${encodeURIComponent(remove.id)}`,
                  "DELETE",
                  {},
                );
                setRemove(null);
                await load();
              } catch (e) {
                setError(errorMessage(e));
                setRemove(null);
              } finally {
                setBusy(false);
              }
            }}
          >
            Remove passkey
          </button>
        </Modal>
      )}
    </section>
  );
}
